import React, { useState, useRef, useEffect } from "react";
import { Message } from "../../hooks/useWebSocketChat";
import MarkdownRenderer from "../common/MarkdownRenderer";
import { ResearchReportRenderer } from "./ResearchReportRenderer";

interface ChatMessageProps {
  message: Message;
  context?: "chat" | "demo-tour";
}

const formatTimestamp = (timestamp?: Date) => {
  if (!timestamp) return "sending...";

  return timestamp.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

export const ChatMessage: React.FC<ChatMessageProps> = ({ message, context = "chat" }) => {
  const [isMarkdownExpanded, setIsMarkdownExpanded] = useState(false);
  const [needsExpansion, setNeedsExpansion] = useState(false);
  const markdownRef = useRef<HTMLDivElement>(null);
  const isChatContext = context === "chat";

  // Check if content needs expansion
  useEffect(() => {
    if (markdownRef.current) {
      const contentHeight = markdownRef.current.scrollHeight;
      const lineHeight = parseInt(getComputedStyle(markdownRef.current).lineHeight || "24");
      const approxLines = contentHeight / lineHeight;

      // If more than 12 lines, it needs expansion
      setNeedsExpansion(approxLines > 12);
    }
  }, [message.raw_text]);

  if (
    message.author_type === "assistant" &&
    message.message_type === "research_report"
  ) {
    return <ResearchReportRenderer reportData={message.raw_text} />;
  }

  if (message.author_type === "system") {
    return (
      <div className="chat-message chat-message--system chat-markdown-container">
        <div className="chat-message__content">
          <p>{message.raw_text}</p>
        </div>
      </div>
    );
  }

  if (message.author_type === "assistant" && message.message_type === "markdown") {
    try {
      return (
        <div
          className="chat-message chat-message--assistant chat-markdown-container"
          style={{
            position: 'relative',
            zIndex: 5,
          }}
        >
          <div className="chat-message__content">
            {needsExpansion ? (
              <>
                <div
                  ref={markdownRef}
                  className={`chat-message__markdown ${isMarkdownExpanded ? 'expanded' : 'collapsed'}`}
                >
                  <MarkdownRenderer markdown={message.raw_text} />
                </div>
                {!isMarkdownExpanded && (
                  <div className="chat-message__expand">
                    <button onClick={() => setIsMarkdownExpanded(true)}>
                      Read more...
                    </button>
                  </div>
                )}
                {isMarkdownExpanded && (
                  <div className="chat-message__collapse">
                    <button onClick={() => setIsMarkdownExpanded(false)}>
                      Collapse
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div
                ref={markdownRef}
                className="chat-message__markdown"
              >
                <MarkdownRenderer markdown={message.raw_text} />
              </div>
            )}
          </div>
        </div>
      );
    } catch (error) {
      console.error('Error rendering markdown message:', error);
      return (
        <div className="chat-message chat-message--assistant chat-markdown-container">
          <div className="chat-message__content">
            <p>{message.raw_text}</p>
          </div>
        </div>
      );
    }
  }

  return (
    <div
      className={`chat-message chat-message--${message.author_type}`}
      style={{
        position: 'relative',
        zIndex: message.author_type === 'assistant' ? 5 : 6,
      }}
    >
      <div className="chat-message__content">
        <p>{message.raw_text}</p>
      </div>
      {isChatContext && (
        <div className="chat-message__timestamp">
          {formatTimestamp(message.timestamp)}
        </div>
      )}
    </div>
  );
};
